import { translate } from '@util/Localization';
import { Progress } from 'antd';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { Testimonial } from '../../styles/Testimonial';
import { BodySM } from '../../styles/Typography';

const TOTAL_LOADING_TIME = process.env.NODE_ENV === 'development' ? 3000 : 10000;

export type LoadingResultsLayoutConfig = {
  reviewer: {
    name: string;
    image?: string;
    testimonial: string[];
    date: string;
  };
  /** Defaults to true */
  showAnalysisText?: boolean;
  textAnimationEnabled?: boolean;
};

type LoadingResultsLayoutProps = LoadingResultsLayoutConfig & {
  onNext: () => void;
};

export const LoadingResultsLayout: FunctionComponent<LoadingResultsLayoutProps> = (props) => {
  const { reviewer, onNext, showAnalysisText = true, textAnimationEnabled = false } = props;

  const [progress, setProgress] = useState<number>(0);
  const progressColor = appOverride({ nerva: Color.content.default, default: Color.action.accent });
  const trailColor = appOverride({
    nerva: 'rgba(245, 245, 245, 0.25)',
    default: Color.surface.muted,
  });

  useEffect(
    function goToNextScreenOnLoadingComplete() {
      if (progress === 100) {
        setTimeout(() => onNext(), 1000);
      }
    },
    [progress, onNext]
  );

  const step = useCallback(
    (time: number, percent: number): Promise<void> => {
      return new Promise((res) => {
        setTimeout(() => {
          setProgress(percent);
          res();
        }, time);
      });
    },
    [setProgress]
  );

  const doProgression = useCallback(
    (percent: number): Promise<void> => {
      const increment = Math.random() * 5 + 1;
      if (percent + increment > 100) {
        return step((increment * TOTAL_LOADING_TIME) / 100, 100);
      } else {
        return step((increment * TOTAL_LOADING_TIME) / 100, percent + increment).then(() =>
          doProgression(percent + increment)
        );
      }
    },
    [step]
  );

  useEffect(
    function startProgression() {
      doProgression(0);
    },
    [doProgression]
  );

  return (
    <div data-test="loadingForm">
      <ProgressSection>
        {showAnalysisText && (
          <BodySM animationEnabled={textAnimationEnabled} animationIndex={0}>
            {analysisText(progress)}
          </BodySM>
        )}
        <div style={{ position: 'relative' }}>
          <LargeProgress
            strokeColor={progressColor}
            trailColor={trailColor}
            percent={progress}
            size="small"
            showInfo={false}
            status="active"
          />
          <ProgressText
            animationEnabled={textAnimationEnabled}
            animationIndex={1}
            progress={progress}
          >
            {progress.toFixed()}%
          </ProgressText>
        </div>
      </ProgressSection>
      <Testimonial {...reviewer} stars={5} />
    </div>
  );
};

function analysisText(progress: number) {
  if (progress <= 22) {
    return 'Connecting to database...';
  } else if (progress <= 48) {
    return 'Analysing demographic data...';
  } else if (progress <= 83) {
    return translate('loadingData');
  } else {
    return 'Preparing results...';
  }
}

const ProgressSection = styled.div`
  margin: ${({ theme }) => `0 0 ${theme.spacing._60px}`};
`;

const ProgressText = styled(BodySM)<{ progress: number }>`
  position: absolute;
  margin: auto;
  margin-top: ${appOverride({ nerva: '-32px', default: '-29px' })};
  height: ${({ theme }) => theme.spacing._32px};
  width: 100%;
  text-shadow: ${appOverride({ nerva: 'none', default: '1px 1px 2px rgba(255, 255, 255, 0.5)' })};
  color: ${({ progress }) =>
    appOverride({
      nerva: progress < 50 ? '#FFFFFF' : '#367B6E',
      default: 'inherit',
    })};
`;

const LargeProgress = styled(Progress)`
  .ant-progress-bg {
    height: 32px !important;
  }

  ${appOverride({
    nerva: `
    .ant-progress-outer {
      border: 2px solid rgba(245, 245, 245, 0.25);
      border-radius: 16px;
      overflow: hidden;
      background: transparent;
    }

    .ant-progress-inner {
      background: transparent !important;
      border-radius: 16px;
    }
  `,
    default: '',
  })}
`;
